import { EdgeResizer } from '~src/utils/EdgeResizer';

export const MAX_SAFE_BACKGROUND_DIMENSION = 4096;

const toPositiveNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number) && number > 0 ? number : null;
};

const normalizeUrl = (url) => {
  if (!url) {
    return null;
  }

  const value = String(url);
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

const isEdgeResizerUrl = (imageUrl) => {
  try {
    const imageOrigin = new URL(imageUrl).origin;
    const edgeResizerOrigin = new URL(normalizeUrl(process.env.EDGE_RESIZER))
      .origin;

    return imageOrigin === edgeResizerOrigin;
  } catch (error) {
    return false;
  }
};

const isRootBucketUrl = (imageUrl) => {
  try {
    const pathname = decodeURIComponent(new URL(imageUrl).pathname);
    const key = pathname.replace(/^\/+|\/+$/g, '');

    return Boolean(key) && !key.includes('/');
  } catch (error) {
    return false;
  }
};

const normalizeDimensions = ({ width, height } = {}) => ({
  width: toPositiveNumber(width),
  height: toPositiveNumber(height),
});

const getLongSide = ({ width, height }) =>
  Math.max(width || 0, height || 0) || null;

const getResizeDimension = ({
  allowUpscale,
  maxDimension,
  sourceDimensions,
  targetDimensions,
}) => {
  const sourceLongSide = getLongSide(sourceDimensions);

  if (!sourceLongSide) {
    return null;
  }

  if (sourceLongSide > maxDimension) {
    return maxDimension;
  }

  if (!allowUpscale) {
    return null;
  }

  const targetLongSide = getLongSide(targetDimensions);

  if (!targetLongSide || targetLongSide <= sourceLongSide) {
    return null;
  }

  return Math.min(targetLongSide, maxDimension);
};

export const hasOversizedBackgroundDimensions = (
  width,
  height,
  maxDimension = MAX_SAFE_BACKGROUND_DIMENSION
) => {
  const normalizedWidth = toPositiveNumber(width);
  const normalizedHeight = toPositiveNumber(height);

  return normalizedWidth > maxDimension || normalizedHeight > maxDimension;
};

export const getSafeBackgroundImageUrl = ({
  allowUpscale = false,
  imageUrl,
  maxDimension = MAX_SAFE_BACKGROUND_DIMENSION,
  sourceDimensions,
  targetDimensions,
}) => {
  if (!imageUrl || !isEdgeResizerUrl(imageUrl)) {
    return imageUrl;
  }

  const normalizedSourceDimensions = normalizeDimensions(sourceDimensions);
  const resizeDimension = getResizeDimension({
    allowUpscale,
    maxDimension,
    sourceDimensions: normalizedSourceDimensions,
    targetDimensions: normalizeDimensions(targetDimensions),
  });

  if (!resizeDimension) {
    return imageUrl;
  }

  if (!isRootBucketUrl(imageUrl)) {
    throw new Error(
      'Gli sfondi elaborati da EdgeResizer devono trovarsi nella root del bucket'
    );
  }

  const resizer = new EdgeResizer(imageUrl);

  if (
    normalizedSourceDimensions.width &&
    (!normalizedSourceDimensions.height ||
      normalizedSourceDimensions.width >= normalizedSourceDimensions.height)
  ) {
    return resizer.scaleToWidth(resizeDimension);
  }

  return resizer.scaleToHeight(resizeDimension);
};
